import FileViewer from "components/file-components/fileViewer2";
import RespondantAssetAppealRequest from "components/pop-up/RespondantAssetAppealRequest";
import { useAuth } from "context/AuthContext";
import { useState, useEffect, useMemo } from "react";
import { getReferralReportsByReferralId } from "services/assetRecoveryService";
import {
  Referral,
  Roles,
  AssetReferralActionStatusEnum,
  Report,
  ProhibitionRequest,
} from "types";
import { hasRole } from "utils/helper";
import { ProhibitionReportTabs } from "./ProhibitionAddReport";
import ReportList from "./ProhitibitonReportList";

export default function ReportContainer({
  referralId,
  referral,
  setActiveTab,
}: {
  referralId: string;
  referral?: Referral;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}) {
  const { loginData } = useAuth();
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const [reports, setReports] = useState<Report[]>([]);
  const [shouldShowReportTabs, setShouldShowReportTabs] = useState(false);
  const [isRespondantPopupOpen, setIsRespondantPopupOpen] = useState(false);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  useEffect(() => {
    getReferralReportsByReferralId(referralId)
      .then((d) => setReports(d))
      .catch(console.error);
  }, [referralId]);

  const showReportTabStatuses = [
    AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION,
  ];

  const {
    showTabs,
    suspectAssetMappingsNeedingReport,
    allSuspectAssetMappings,
  } = useMemo(() => {
    // Get all suspect_asset_mappings (these are the ones with assets)
    const suspectAssetMappings =
      referral?.suspect_asset_counts?.filter(
        (mapping) => mapping.assets && mapping.assets.length > 0,
      ) ?? [];

    if (!isAssetProsecutor) {
      return {
        showTabs: false,
        suspectAssetMappingsNeedingReport: [],
        allSuspectAssetMappings: suspectAssetMappings,
      };
    }

    const finalProhibition = reports.filter(
      (r) => r.is_next_appointment_reason_final_verdict,
    );

    // Get all suspect_asset_mapping IDs that are already covered by final prohibition reports
    const coveredSuspectAssetMappingIds = new Set<string>();
    finalProhibition.forEach((report) => {
      if (report.asset) {
        coveredSuspectAssetMappingIds.add(report.asset.id as string);
      }
    });

    // Filter out suspect_asset_mappings that are already covered by final reports
    const needing = suspectAssetMappings.filter(
      (mapping) => !coveredSuspectAssetMappingIds.has(mapping.id),
    );

    const isProhibitionCase = referral?.prohibition_record?.some(
      (record: ProhibitionRequest) =>
        record.status ===
        AssetReferralActionStatusEnum.PROHIBITION_ON_LITIGATION,
    );

    let show = false;

    if (isProhibitionCase) {
      // Always show tabs for prohibition cases
      show = true;
    } else {
      // For other cases, use the original logic if needed
      const hasSuspectAssetMappings = suspectAssetMappings.length > 0;
      const hasMappingsNeedingReport = needing.length > 0;
      show = hasSuspectAssetMappings || hasMappingsNeedingReport;
    }

    return {
      showTabs: show,
      suspectAssetMappingsNeedingReport: needing,
      allSuspectAssetMappings: suspectAssetMappings,
    };
  }, [referral, reports, isAssetProsecutor]);

  useEffect(() => {
    setShouldShowReportTabs(showTabs);
  }, [showTabs]);

  const handleReportDataUpdated = async () => {
    const proh = await getReferralReportsByReferralId(referralId);
    setReports(proh);
  };

  return (
    <div className="flex gap-3">
      <ReportList
        referralId={referralId}
        fileRowProps={fileRowProps}
        reports={reports}
        handleReportDataUpdated={handleReportDataUpdated}
      />

      <div className="flex flex-col">
        {isAssetRegistrar &&
          reports.some(
            (report) => report.is_next_appointment_reason_final_verdict,
          ) && (
            <div className="flex justify-end self-end mt-5">
              <button
                type="submit"
                className="referral-form-button text-white px-8 py-3"
                onClick={() => setIsRespondantPopupOpen(true)}
              >
                Request Appeal
              </button>
            </div>
          )}
        {shouldShowReportTabs &&
          isAssetProsecutor &&
          referral &&
          referral.prohibition_record &&
          showReportTabStatuses.some((status) =>
            referral.prohibition_record?.some(
              (record: ProhibitionRequest) => record.status === status,
            ),
          ) && (
            <div className="">
              <ProhibitionReportTabs
                referralId={referralId}
                suspectAssetMappings={referral?.suspect_asset_counts}
                suspectAssetMappingsNeedingReport={
                  suspectAssetMappingsNeedingReport
                }
                prohibitionRecord={referral.prohibition_record.filter(
                  (record: ProhibitionRequest) =>
                    showReportTabStatuses.includes(
                      record.status as AssetReferralActionStatusEnum,
                    ),
                )}
                assetsNeedingReport={[]}
                setActiveTab={setActiveTab}
                onNewDataAdded={handleReportDataUpdated}
                referral={referral as Referral}
                reports={reports as Report[]}
              />
            </div>
          )}
      </div>

      <FileViewer
        selectedApplicantDocument={selectedDocument ?? undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
      <RespondantAssetAppealRequest
        isOpen={isRespondantPopupOpen}
        onClose={() => setIsRespondantPopupOpen(false)}
        asset={allSuspectAssetMappings.flatMap((mapping) => mapping.assets)}
        prohabition_request_court_registration_number={
          referral?.prohabition_request_court_registration_number as string
        }
        asset_referral={referralId}
        onSuccess={handleReportDataUpdated}
      />
    </div>
  );
}
